import { useMemo, useState } from "react";
import { Loader2, X, UserPlus } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { OPS } from "@/constants/testIds";

const NONE = "__none__";

const rowKey = (r, i) => (r.employee_id ? `hr:${r.employee_id}` : `manual:${(r.full_name || "").toLowerCase()}:${i}`);

/**
 * Responsible employees for ONE violation — one or MORE people.
 *
 * Every HR-picked employee is pushed to HR as their own violation record, so
 * each of them can be judged separately there. A typed (manual) name is kept
 * in Ops only and is marked unverified — it never becomes an HR link.
 */
export function ResponsibleEmployees({
    value, employees, employeesAvailable, employeesLoaded, canEdit, onChange, prefix, t,
}) {
    const rows = useMemo(() => (Array.isArray(value) ? value : []), [value]);
    const [pick, setPick] = useState("");
    const [manual, setManual] = useState("");

    const takenIds = useMemo(() => new Set(rows.map((r) => r.employee_id).filter(Boolean)), [rows]);
    const options = useMemo(
        () => (employees || []).filter((e) => !takenIds.has(e.employee_id)),
        [employees, takenIds],
    );

    const addEmployee = (id) => {
        const emp = (employees || []).find((e) => e.employee_id === id);
        setPick("");
        if (!emp) return;
        onChange([...rows, {
            employee_id: emp.employee_id,
            full_name: emp.full_name,
            job_title: emp.job_title || null,
            source: "hr",
        }]);
    };

    const addManual = () => {
        const nm = (manual || "").trim();
        if (!nm) return;
        setManual("");
        if (rows.some((r) => !r.employee_id && (r.full_name || "").trim().toLowerCase() === nm.toLowerCase())) return;
        onChange([...rows, { employee_id: null, full_name: nm, job_title: null, source: "manual" }]);
    };

    const removeAt = (idx) => onChange(rows.filter((_, i) => i !== idx));

    if (!employeesLoaded) {
        return (
            <div className="inline-flex items-center gap-1.5 text-[11px] text-[color:var(--ink-muted)] h-9">
                <Loader2 className="h-3 w-3 animate-spin" />
                {t("qc_ins_responsible_loading")}
            </div>
        );
    }

    return (
        <div data-testid={OPS.qcIns.responsible.root(prefix)} className="space-y-1.5">
            {rows.length > 0 ? (
                <div className="flex flex-wrap gap-1.5">
                    {rows.map((r, i) => (
                        <Badge
                            key={rowKey(r, i)}
                            data-testid={OPS.qcIns.responsible.chip(prefix, i)}
                            variant={r.employee_id ? "secondary" : "warning"}
                            className="gap-1 max-w-full"
                        >
                            <bdi className="truncate">
                                {r.full_name}
                                {r.job_title ? ` — ${r.job_title}` : ""}
                                {!r.employee_id ? ` (${t("qc_ins_responsible_unverified")})` : ""}
                            </bdi>
                            {canEdit ? (
                                <button
                                    type="button"
                                    onClick={() => removeAt(i)}
                                    aria-label={t("qc_ins_responsible_remove")}
                                    data-testid={OPS.qcIns.responsible.remove(prefix, i)}
                                    className="rounded-full hover:bg-black/10 p-0.5 transition-colors"
                                >
                                    <X className="h-3 w-3" />
                                </button>
                            ) : null}
                        </Badge>
                    ))}
                </div>
            ) : (
                <p className="text-[10px] text-[color:var(--ink-muted)]">{t("qc_ins_responsible_none_assigned")}</p>
            )}

            {canEdit && employeesAvailable ? (
                <Select value={pick || undefined} onValueChange={addEmployee}>
                    <SelectTrigger
                        data-testid={OPS.qcIns.responsible.add(prefix)}
                        className="h-9 text-sm"
                    >
                        <SelectValue placeholder={t("qc_ins_responsible_add")} />
                    </SelectTrigger>
                    <SelectContent>
                        {options.length === 0 ? (
                            <SelectItem value={NONE} disabled>{t("qc_ins_responsible_all_added")}</SelectItem>
                        ) : options.map((e) => (
                            <SelectItem key={e.employee_id} value={e.employee_id}>
                                <bdi>{e.full_name}{e.job_title ? ` — ${e.job_title}` : ""}</bdi>
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            ) : null}

            {canEdit && !employeesAvailable ? (
                <>
                    <p
                        data-testid={OPS.qcIns.responsible.hrNote(prefix)}
                        className="text-[10px] leading-snug text-[color:var(--warning-soft-fg,#b45309)]"
                    >
                        {t("qc_ins_responsible_hr_unavailable")}
                    </p>
                    <div className="flex items-center gap-1.5">
                        <Input
                            data-testid={OPS.qcIns.responsible.manualInput(prefix)}
                            value={manual}
                            onChange={(e) => setManual(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addManual(); } }}
                            placeholder={t("qc_ins_responsible_manual_ph")}
                            className="h-9 text-sm"
                        />
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={addManual}
                            disabled={!manual.trim()}
                            data-testid={OPS.qcIns.responsible.manualAdd(prefix)}
                            className="shrink-0 gap-1"
                        >
                            <UserPlus className="h-3.5 w-3.5" />
                            {t("qc_ins_responsible_manual_add")}
                        </Button>
                    </div>
                </>
            ) : null}

            {canEdit ? (
                <p
                    data-testid={OPS.qcIns.responsible.gateNote(prefix)}
                    className="text-[10px] leading-snug text-[color:var(--ink-muted)]"
                >
                    {t("qc_ins_responsible_multi_hint")}
                </p>
            ) : null}
        </div>
    );
}

export default ResponsibleEmployees;
